import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import Table from '../table';
import CompetitiveIndex from '../competitive-index';
import GenericObjectArrayProps from '../../prop-types/generic-object';
import BPLink from '../bp-link';
import WatchStation from '../watch-station';
import StatComparison from '../stat-comparison';
import ExportModal from '../export-modal';

import { compareCompetitiveIndex, compareNumber, compareString } from '../table/sort-helpers';

import styles from './styles.css';

export default function BrandEngagementTable({
  isLoading: isLoadingProps,
  data,
  comparisonData,
  stations,
  onExport,
  comparison,
  onPageChange,
  onSortChange,
  pageSize,
  pageNumber,
}) {
  const [brandEngagement, setBrandEngagement] = React.useState(null);
  const [showExportModal, setShowExportModal] = React.useState(false);

  const BRAND_ENGAGEMENT_COLUMNS = [
    {
      title: '',
      render: template => (
        <WatchStation stationId={template.id} isWatched={template.watched} onToggle={handleOnWatchStationToggle} className={styles.watchIcon} />
      ),
      value: template => template.watched,
      colSpan: 1,
    },
    {
      title: 'Competitive Index',
      renderTableCell: template => (
        <StatComparison
          value={template.competitive_index}
          comparisonValue={template.comparison?.competitive_index}
          renderValue={compIndex => (<CompetitiveIndex value={compIndex} size="sm" />)}
          renderComparisonValue={formatComparisonCompetitiveIndex}
          competitiveIndex
        />
      ),
      value: template => template.competitive_index,
      colSpan: 2,
      sortFn: compareCompetitiveIndex,
    },
    {
      title: 'Station',
      render: template => (
        <BPLink to={`/client/station-profiles/station-profile?stationId=${template.id}`}>
          {`#${template.retailer_reference} - ${template.retailer_name}`}
        </BPLink>
      ),
      value: template => `{${template.retailer_reference} - ${template.retailer_name}}`,
      colSpan: 2,
      sortFn: compareString,
    },
    {
      title: 'Brand Impressions',
      renderTableCell: template => (<StatComparison value={template.search_impressions} comparisonValue={template.comparison?.search_impressions} />),
      value: template => template.search_impressions,
      colSpan: 2,
      sortFn: compareNumber,
    },
    {
      title: 'Brand Engagement',
      renderTableCell: template => (<StatComparison value={template.brand_engagement} comparisonValue={template.comparison?.brand_engagement} />),
      value: template => template.brand_engagement,
      colSpan: 2,
      sortFn: compareNumber,
    },
    {
      title: 'Station Visits',
      renderTableCell: template => (<StatComparison value={template.station_visits} comparisonValue={template.comparison?.station_visits} />),
      value: template => template.station_visits,
      colSpan: 2,
      sortFn: compareNumber,
    },
    {
      title: 'Avg Dwell Time',
      renderTableCell: template => (
        <StatComparison
          value={template.avg_dwell_time}
          comparisonValue={template.comparison?.avg_dwell_time}
          renderValue={formatDwellTime}
          renderComparisonValue={formatDwellTime}
        />
      ),
      value: template => template.avg_dwell_time,
      colSpan: 2,
      sortFn: compareNumber,
    },
  ];

  const [isLoading, setIsLoading] = React.useState(isLoadingProps);

  React.useEffect(() => {
    if (!isLoadingProps) {
      populateBrandEnagementMap();
    }
    setIsLoading(isLoadingProps);
  }, [isLoadingProps]);

  const formatComparisonCompetitiveIndex = compIndex => (compIndex > 0 ? compIndex : 'N/A');

  const populateBrandEnagementMap = () => {
    const singleStationComparison = (comparison === 'Prior Period' || comparison === 'Nearby Stations');

    const map = {};
    const stationMap = _.keyBy(stations, 'id');
    const engagementMap = _.keyBy(data, 'id');
    let comparisonEngagementMap = {};

    if (singleStationComparison) {
      comparisonEngagementMap = _.keyBy(comparisonData, 'id');
    }

    Object.keys(stationMap).map(Number).forEach((stationId) => {
      map[stationId] = {
        ...stationMap[stationId],
        ...engagementMap[stationId],
        comparison: singleStationComparison ? comparisonEngagementMap[stationId] : comparisonData,
      };
    });
    setBrandEngagement(Object.values(map));
  };

  const formatDwellTime = (dwellTime = 0) => `${Math.floor(dwellTime / 60)}m ${dwellTime % 60}s`;

  const handleOnWatchStationToggle = ({ id, watched }) => {
    const affectedIndex = brandEngagement.findIndex(be => be.id === id);
    brandEngagement[affectedIndex].watched = watched;
    setBrandEngagement(brandEngagement);
  };

  const handleOnExportClick = () => {
    setShowExportModal(true);
  };

  return (
    <>
      <Table
        title="Station Performance Breakdown"
        loading={isLoading}
        paginator
        data={brandEngagement}
        tableColumns={BRAND_ENGAGEMENT_COLUMNS}
        defaultSortColumn={BRAND_ENGAGEMENT_COLUMNS[2].title}
        prioritySortOrder
        minWidth={800}
        onExport={onExport}
        onCTAClick={handleOnExportClick}
        CTAText="Export Data"
        onPageChange={onPageChange}
        onSortChange={onSortChange}
        pageNumber={pageNumber}
        pageSize={pageSize}
      />
      <ExportModal show={showExportModal} onClose={() => setShowExportModal(false)} onExport={onExport} />
    </>
  );
}

BrandEngagementTable.propTypes = {
  isLoading: PropTypes.bool,
  data: GenericObjectArrayProps,
  comparisonData: GenericObjectArrayProps,
  stations: GenericObjectArrayProps,
  onExport: PropTypes.func,
  comparison: PropTypes.string,
  onPageChange: PropTypes.func,
  onSortChange: PropTypes.func,
  pageSize: PropTypes.number,
  pageNumber: PropTypes.number,
};

BrandEngagementTable.defaultProps = {
  isLoading: true,
  data: null,
  comparisonData: null,
  stations: null,
  onExport: () => { },
  comparison: null,
  onPageChange: () => { },
  onSortChange: () => { },
  pageSize: 25,
  pageNumber: 1,
};
